/**
 * Mock for @nativescript/core
 * Provides empty implementations for web compatibility
 */

// HTTP module mock
export const Http = {
  request: async () => ({ content: null, statusCode: 0 }),
  getFile: async () => null,
  getImage: async () => null,
  getJSON: async () => null,
  getString: async () => '',
}

// Application module mock
export const Application = {
  android: null,
  ios: null,
  on: () => {},
  off: () => {},
  run: () => {},
}

// ApplicationSettings mock (localStorage-based for web)
export const ApplicationSettings = {
  getString: (key: string, defaultValue?: string) => {
    try {
      return localStorage.getItem(key) ?? defaultValue ?? ''
    } catch {
      return defaultValue ?? ''
    }
  },
  setString: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value)
    } catch {}
  },
  getNumber: (key: string, defaultValue?: number) => {
    try {
      const val = localStorage.getItem(key)
      return val !== null ? parseFloat(val) : (defaultValue ?? 0)
    } catch {
      return defaultValue ?? 0
    }
  },
  setNumber: (key: string, value: number) => {
    try {
      localStorage.setItem(key, String(value))
    } catch {}
  },
  getBoolean: (key: string, defaultValue?: boolean) => {
    try {
      const val = localStorage.getItem(key)
      return val !== null ? val === 'true' : (defaultValue ?? false)
    } catch {
      return defaultValue ?? false
    }
  },
  setBoolean: (key: string, value: boolean) => {
    try {
      localStorage.setItem(key, String(value))
    } catch {}
  },
  remove: (key: string) => {
    try {
      localStorage.removeItem(key)
    } catch {}
  },
  clear: () => {
    try {
      localStorage.clear()
    } catch {}
  },
  hasKey: (key: string) => {
    try {
      return localStorage.getItem(key) !== null
    } catch {
      return false
    }
  },
}

// CoreTypes mock
export const CoreTypes = {
  KeyboardType: {
    datetime: 'datetime',
    phone: 'phone',
    number: 'number',
    url: 'url',
    email: 'email',
    integer: 'integer',
  },
  ReturnKeyType: {
    done: 'done',
    next: 'next',
    go: 'go',
    search: 'search',
    send: 'send',
  },
  TextAlignment: {
    left: 'left',
    center: 'center',
    right: 'right',
  },
  Orientation: {
    horizontal: 'horizontal',
    vertical: 'vertical',
  },
  DeviceOrientation: {
    portrait: 'portrait',
    landscape: 'landscape',
    unknown: 'unknown',
  },
  HorizontalAlignment: {
    left: 'left',
    center: 'center',
    right: 'right',
    stretch: 'stretch',
  },
  VerticalAlignment: {
    top: 'top',
    center: 'center',
    bottom: 'bottom',
    stretch: 'stretch',
  },
  Stretch: {
    none: 'none',
    fill: 'fill',
    aspectFit: 'aspectFit',
    aspectFill: 'aspectFill',
  },
  Visibility: {
    visible: 'visible',
    collapse: 'collapse',
    collapsed: 'collapsed',
    hidden: 'hidden',
  },
  FontStyle: {
    normal: 'normal',
    italic: 'italic',
  },
  FontWeight: {
    thin: '100',
    extraLight: '200',
    light: '300',
    normal: '400',
    medium: '500',
    semiBold: '600',
    bold: '700',
    extraBold: '800',
    black: '900',
  },
}

// Utils mock
export const Utils = {
  isAndroid: false,
  isIOS: false,
  isMacOS: false,
  isWindows: false,
  isRealDevice: () => false,
  layout: {
    toDevicePixels: (value: number) => value,
    toDeviceIndependentPixels: (value: number) => value,
  },
}

// Observable mock
export class Observable {
  on() {}
  off() {}
  notify() {}
  get() { return undefined }
  set() {}
}

// Device mock
export const Device = {
  manufacturer: 'Browser',
  model: 'Web',
  os: 'Web',
  osVersion: navigator.userAgent,
  sdkVersion: '1.0',
  deviceType: 'Desktop',
  uuid: 'web-browser',
  language: navigator.language,
  region: navigator.language.split('-')[1] || 'US',
}

// Screen mock
export const Screen = {
  mainScreen: {
    widthDIPs: window.innerWidth,
    heightDIPs: window.innerHeight,
    widthPixels: window.innerWidth * window.devicePixelRatio,
    heightPixels: window.innerHeight * window.devicePixelRatio,
    scale: window.devicePixelRatio,
  },
}

// isAndroid / isIOS
export const isAndroid = false
export const isIOS = false

// Export empty defaults for any other imports
export default {}
